/**
 * NotificationsProvider — owns the single Firestore notification subscription
 * for the authenticated user and exposes it via context.
 *
 * Mounted once inside the protected layout so:
 *   - the NavBar bell reads the same live list / unread count, and
 *   - new notifications raise a design-system toast app-wide (regardless of
 *     whether the dropdown is open), matching how chat surfaces messages.
 */
import * as React from 'react';
import { useAuth } from '@finmin/auth';
import { useToast } from '@finmin/ui';
import { useFirebaseNotifications } from './hooks/use-firebase-notifications';
import { getEventMeta } from './event-meta';
import type { AppNotification, NotificationStatus } from './types';

interface NotificationsContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  status: NotificationStatus;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

const NotificationsContext = React.createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();

  const handleNew = React.useCallback(
    (n: AppNotification) => {
      const { variant } = getEventMeta(n.event);
      const title = n.title || 'New notification';
      const body = n.body || undefined;

      switch (variant) {
        case 'success':
          toast.success(title, body);
          break;
        case 'warning':
          toast.warning(title, body);
          break;
        case 'error':
          toast.error(title, body);
          break;
        default:
          toast({ title, description: body });
      }
    },
    [toast],
  );

  const { notifications, unreadCount, status, markAsRead, markAllAsRead } =
    useFirebaseNotifications({ userId: user?.id, onNew: handleNew });

  const value = React.useMemo<NotificationsContextValue>(
    () => ({ notifications, unreadCount, status, markAsRead, markAllAsRead }),
    [notifications, unreadCount, status, markAsRead, markAllAsRead],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsContextValue {
  const ctx = React.useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within <NotificationsProvider>');
  }
  return ctx;
}
